/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useMemo, useState } from "react";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { GenericCard } from "@ui-components/Card/GenericCard";
import { GenericTable } from "@ui-components/Table/GenericTable";
import { InternetDetailSection } from "./InternetDetailsData";

import { GenericTag } from "@ui-components/Tag/GenericTag";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import ServiceStatusValue from "kontrolEdge/components/Shared/ServiceStatusValue";
import { getTextColor } from "kontrolEdge/helpers/getTextColor";
import { useMetaEditor } from "kontrolEdge/components/Shared/NameTagEdit/useMetaEditor";
import { EditableTagList } from "kontrolEdge/components/Shared/NameTagEdit/EditableTagList";
import type { ResourceTagSummary } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";

interface Props {
  data: InternetDetailSection;
  onMetaPatched?: (p: { name?: string; tag?: string }) => void;
  latencyLoading?: boolean;
  connectionDetail?: {
    port?: {
      id: number;
      name: string;
    };
    lag?: {
      id?: number;
      subid?: string;
    } | null;
    sdwanHub?: {
      id: number;
      name: string;
      subscriptionId: string | null;
    } | null;
  };
  /** Pre-hydrated tag list from the detail GET inline `tags` field (§5.5). */
  initialAssignedTags?: ResourceTagSummary[];
  /** Tenant the resource actually lives in (`detail.tenantId`). See PortDetails.tsx for rationale. */
  resourceTenantId?: number;
}

const InternetDetailsCard: React.FC<Props> = ({
  data,
  onMetaPatched,
  latencyLoading,
  connectionDetail,
  initialAssignedTags,
  resourceTenantId,
}) => {
  const navigate = useNavigate();
  const api = useAxiosAuth0();
  //Port id selected from MainPort
  const [searchParams] = useSearchParams();
  const connectionId = searchParams.get("connectionId") ?? null;
  const { tenantId } = useAppSelector((state) => state.selection);
  const shouldCollapse = data.isCollapsible ?? true;

  // Logic for Name and Tag editing
  const nameSeed = String(
    data.items.find((i) => i.label === "Connection Name")?.value ?? ""
  );
  const tagSeed = (() => {
    const v = data.items.find((i) => i.label === "TAG")?.value;
    return Array.isArray(v) ? v.join(", ") : String(v ?? "");
  })();

  const meta = useMetaEditor({
    api,
    kind: "internet",
    resourceId: connectionId,
    tenantId,
    initialName: nameSeed,
    initialTag: tagSeed,
    onPatched: onMetaPatched,
  });

  // Convert tag string to array for EditableTagList
  const tagArray = useMemo(() => {
    if (!meta.tagValue || meta.tagValue.trim() === "") return [];
    return meta.tagValue.split(",").map(t => t.trim()).filter(t => t !== "");
  }, [meta.tagValue]);
  

  // Inline spinner
  const InlineSpinner = () => (
    <span className="inline-block h-4 w-4 align-middle">
      <span className="block h-4 w-4 rounded-full border-2 border-gray-300 border-t-gray-500 animate-spin" />
    </span>
  );

  if (data.table) {
    return (
      <>
        <GenericCard
          headerContent={data.header}
          isCollapsible
          bodyContent={
            <div className="space-y-2">
              {data.items
                .filter((item) => item.type === "subtitle")
                .map((item, i) => (
                  <h4
                    key={i}
                    className="text-sm font-medium text-semantic-text-secondary"
                  >
                    {item.label}
                  </h4>
                ))}
              <GenericTable
                columns={data.table.columns}
                data={data.table.data}
                tableClassName="w-full"
                isFooterVisible={false}
              />
            </div>
          }
        />
      </>
    );
  }
  return (
    <>
      <GenericCard
        isCollapsible={shouldCollapse}
        headerContent={data.header}
        bodyContent={
          <div className="space-y-2">
            {data.items.map((item, idx) => {
              if (item.type === "subtitle") {
                return (
                  <div key={idx} className="pt-3 pb-1">
                    <h4 className="text-sm font-medium text-semantic-text-secondary">
                      {item.label}
                    </h4>
                  </div>
                );
              }

              // 2) Connection Name
              if (item.label === "Connection Name") {
                return (
                  <div
                    key={idx}
                    className="flex justify-between w-full py-1 border-b"
                  >
                    <h3 className="text-sm text-semantic-text-primary">
                      Connection Name
                    </h3>
                    <div className="flex items-center gap-2">
                      <GenericTag
                        label={meta.nameValue || "—"}
                        variant="neutral"
                        size="small"
                        minWidth="100px"
                        className="text-center"
                        editable={true}
                        value={meta.nameValue}
                        onSave={(newValue) => {
                          meta.setNameValue(newValue);
                          meta.saveName(newValue);
                        }}
                        onCancel={() => {
                          meta.cancelName();
                        }}
                        isSaving={meta.savingField === "name"}
                        disabled={meta.savingField !== null}
                      />
                    </div>
                  </div>
                );
              }

              // 3) TAG
              if (item.label === "TAG") {
                return (
                  <div
                    key={idx}
                    className="flex justify-between w-full py-1 border-b"
                  >
                    <h3 className="text-sm text-semantic-text-primary">TAG</h3>
                    <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                      <EditableTagList
                        tags={tagArray}
                        onUpdate={meta.updateTagArray}
                        disabled={meta.savingField !== null}
                        isLoading={meta.isSavingTag}
                        resourceType="ke-internet"
                        resourceId={connectionId}
                        initialAssignedTags={initialAssignedTags}
                        tenantId={resourceTenantId}
                      />
                    </div>
                  </div>
                );
              }

              // 4) Latency
              if (item.label.startsWith("Latency")) {
                // If suffix exists, it's the dropdown (multiple destinations)
                const hasDropdown = Boolean(item.suffix);
                // Get numeric latency value
                const latencyValue =
                  typeof item.numericValue === "number"
                    ? item.numericValue
                    : (() => {
                        const m = String(item.value ?? "").match(
                          /-?\d+(\.\d+)?/
                        );
                        return m ? parseFloat(m[0]) : NaN;
                      })();
                // Determine color based on thresholds
                let colorClass = "text-semantic-text-primary"; // default
                if (!Number.isNaN(latencyValue)) {
                  if (latencyValue <= 60) {
                    colorClass = "text-semantic-text-brand";
                  } else if (latencyValue >= 61) {
                    colorClass = "text-yellow-600";
                  }
                }
                return (
                  <div
                    key={idx}
                    className={`w-full py-1 ${
                      idx !== data.items.length - 1 ? "border-b" : ""
                    }`}
                  >
                    {/* Header row: label + tooltip + (dropdown or single-destination note) on the LEFT */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm text-semantic-text-primary">
                          {item.label}
                        </h3>
                        <GenericTooltip text="Latency measured from our routers using our own IPs over the client's internet link. Reflects real connectivity conditions." />
                        {hasDropdown ? (
                          <div>{item.suffix}</div>
                        ) : item.destination ? (
                          <span className="text-sm text-semantic-text-secondary">
                            ({item.destination})
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2">
                        {/* spinner during fetch */}
                        {latencyLoading ? (
                          <InlineSpinner />
                        ) : (
                          <>
                            <span
                              className={`text-sm font-medium ${colorClass}`}
                            >
                              {item.value ?? "-"}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              if (item.label.startsWith("CRC Errors")) {
                return (
                  <div
                    key={idx}
                    className={`w-full py-1 ${
                      idx !== data.items.length - 1 ? "border-b" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm text-semantic-text-primary">
                          {item.label}
                        </h3>
                        <GenericTooltip text="Total CRC errors counted in the last hour." />
                      </div>
                      <p className="text-sm">{item.value || "-"}</p>
                    </div>
                  </div>
                );
              }

              // Parent Connection - make it clickeable to navigate to port details
              if (item.label === "Parent Connection") {
                const parentConnectionValue = String(item.value || "-");
                // Use port.id or lag.id if available
                const portId = connectionDetail?.port?.id;
                const lagId = connectionDetail?.lag?.id;
                const sdwanHubId = connectionDetail?.sdwanHub?.id;
                const parentConnectionId = portId || lagId || sdwanHubId;
                const isSdwanBased = sdwanHubId != null && portId == null && lagId == null;

                // Only make it clickeable if we have a parentConnectionId
                if (
                  parentConnectionId != null &&
                  parentConnectionValue !== "-"
                ) {
                  return (
                    <div
                      key={idx}
                      className={`flex justify-between w-full py-1 ${
                        idx !== data.items.length - 1 ? "border-b" : ""
                      }`}
                    >
                      <h3 className="text-sm text-semantic-text-primary">
                        {item.label}
                      </h3>
                      <div className="flex items-center gap-2">
                        <button
                          {...viewHref(isSdwanBased ? "/kontrol/sdwan-hub" : buildDetailPath("port", { portId: parentConnectionId }))}
                          onClick={() => {
                            if (isSdwanBased) {
                              navigate("/kontrol/sdwan-hub");
                              return;
                            }
                            navigate(
                              buildDetailPath("port", { portId: parentConnectionId }),
                              {
                                state: {
                                  portType: undefined as
                                    | "physical"
                                    | "virtual"
                                    | undefined,
                                },
                              }
                            );
                          }}
                          className="
                            hover:text-semantic-text-brand
                            hover:underline
                            focus:underline
                            active:underline
                            font-medium
                            underline
                            text-semantic-text-brand
                            text-sm
                          "
                        >
                          {parentConnectionValue}
                        </button>
                      </div>
                    </div>
                  );
                }
                // If no parentConnectionId, render as normal text
                return (
                  <div
                    key={idx}
                    className={`flex justify-between w-full py-1 ${
                      idx !== data.items.length - 1 ? "border-b" : ""
                    }`}
                  >
                    <h3 className="text-sm text-semantic-text-primary">
                      {item.label}
                    </h3>
                    <p
                      className={`text-sm ${getTextColor(
                        item.label,
                        String(item.value || "")
                      )}`}
                    >
                      {item.value || "-"}
                    </p>
                  </div>
                );
              }

              // Default: render a non-editable key-value row
              return (
                <div
                  key={idx}
                  className={`flex justify-between w-full py-1 ${
                    idx !== data.items.length - 1 ? "border-b" : ""
                  }`}
                >
                  <h3 className="text-sm text-semantic-text-primary">
                    {item.label}
                  </h3>
                  {item.statusVariant ? (
                    <ServiceStatusValue
                      label={String(item.value ?? "-")}
                      variant={item.statusVariant}
                      tooltip={item.tooltip}
                    />
                  ) : item.type === "tag" ? (
                    <GenericTag label={String(item.value)} variant="success" />
                  ) : item.tooltip ? (
                    <GenericTooltip
                      text={item.tooltip}
                      position="bottom"
                      horizontalAlign="right"
                      width="20rem"
                    >
                      <span
                        className={`text-sm ${getTextColor(
                          item.label,
                          String(item.value || "")
                        )}`}
                      >
                        {item.value || "-"}
                      </span>
                    </GenericTooltip>
                  ) : (
                    <p
                      className={`text-sm ${getTextColor(
                        item.label,
                        String(item.value || "")
                      )}`}
                    >
                      {item.value || "-"}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        }
      />
    </>
  );
};

export default InternetDetailsCard;
